import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { OfflineImage } from "@/components/common/offline-image/offline-image";
import { IssueDatePill } from "@/components/issues/issue-date-pill/issue-date-pill";
import { IssueStatusPill } from "@/components/issues/issue-status-pill/issue-status-pill";
import { personName } from "@/lib/issues";
import { cn } from "@/lib/utils";
import type {
  IssueFieldChange,
  IssueUpdate,
  ProjectIssue,
  ProjectImage,
} from "@/types/api";

interface IssueActivityTrailProps {
  issue: ProjectIssue;
  onOpenPhoto?: (images: ProjectImage[], index: number) => void;
}

/** Milliseconds for a sort, or `null` when the value cannot be read as a time. */
function timeOf(value: string | null): number | null {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Updates newest first, on a copy.
 *
 * The copy is load-bearing, not defensive habit: `issue.updates` is TanStack
 * Query cache data, and sorting or reversing it in place would reorder the
 * cached object under every other reader of that query.
 *
 * Sorted on each update's own timestamp rather than reversing the server's
 * order, because nothing in the data contract promises one ([[issues.spec]]).
 * An unreadable timestamp sorts last — that entry already renders without a
 * time, and it must not also displace the genuinely most recent one.
 */
function newestFirst(updates: IssueUpdate[]): IssueUpdate[] {
  return [...updates].sort((a, b) => {
    const left = timeOf(a.created_at);
    const right = timeOf(b.created_at);
    if (left === right) return 0;
    if (left === null) return 1;
    if (right === null) return -1;
    return right - left;
  });
}

/**
 * The append-only history of an issue.
 *
 * Rendered in full, never truncated or collapsed: the trail is the artefact
 * this feature exists to produce, and "show 12 more" on the thing that settles
 * a dispute is the wrong default. See [[issues.spec]].
 *
 * Newest first — an issue being chased is read for what happened last, and with
 * no truncation the old order buried that under the whole history.
 *
 * Two shapes hang off the rule: an `update`, which is someone acting on the
 * issue, and an `edit`, which is someone amending its header. Both are the same
 * row on the wire; only the rendering differs.
 *
 * The raised entry is synthesised from the issue itself rather than stored — an
 * issue's creation is not an `IssueUpdate` on the server, but a timeline missing
 * its beginning reads as though something was lost. It renders last, because
 * under a newest-first sort the raise is the oldest event on the issue.
 */
export function IssueActivityTrail({
  issue,
  onOpenPhoto,
}: IssueActivityTrailProps) {
  const updates = newestFirst(issue.updates ?? []);

  return (
    // `reversed`: the list runs newest to oldest. No visual effect — the
    // markers are not rendered — but it is what the order actually is.
    <ol
      reversed
      className="relative ml-2 flex flex-col border-l border-border pl-5"
    >
      {updates.map((update) =>
        update.kind === "edit" ? (
          <EditEntry key={update.id} update={update} />
        ) : (
          <UpdateEntry
            key={update.id}
            update={update}
            onOpenPhoto={onOpenPhoto}
          />
        ),
      )}
      {/*
        No body, deliberately. The raise entry must not read from mutable
        state: it used to render `issue.description`, so editing the
        description retroactively rewrote what the oldest entry claimed the
        issue was raised with — directly beneath an "Edited" row saying
        otherwise. The description has its own section on the page, and the
        original text is recoverable from the first description edit's old
        value. See [[issue-activity-trail.spec]].
      */}
      <TrailEntry
        dotClass="bg-muted-foreground/50"
        heading={`Raised by ${personName(issue.created_by)}`}
        timestamp={issue.created_at}
      />
    </ol>
  );
}

function UpdateEntry({
  update,
  onOpenPhoto,
}: {
  update: IssueUpdate;
  onOpenPhoto?: (images: ProjectImage[], index: number) => void;
}) {
  return (
    <li className="relative pb-5 last:pb-0">
      <Dot status={update.status} />
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[13px] font-medium tracking-[-0.005em]">
          {personName(update.author)}
        </span>
        <IssueStatusPill status={update.status} />
        <TargetDateChip value={update.target_date} />
      </div>
      <Timestamp value={update.created_at} />
      {update.remarks && (
        <p className="mt-1.5 whitespace-pre-line text-[13px] leading-[19px] text-foreground [text-wrap:pretty]">
          {update.remarks}
        </p>
      )}
      {update.images.length > 0 && (
        <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
          {update.images.map((image, index) => {
            // Anonymous CORS via OfflineImage, matching the photo viewer that
            // loads the same S3 thumbnail; a no-CORS load here poisons the
            // cache entry the viewer then fails on. See [[s3-image-fetching]].
            const thumbnail = (
              <OfflineImage
                assetKey=""
                src={image.thumbnail_url ?? image.annotated_url ?? ""}
                alt=""
                loading="lazy"
                className="h-16 w-16 rounded-md object-cover"
              />
            );

            // Only a button when the host can actually act on the tap. Wrapping
            // it unconditionally is how this shipped tappable-but-inert: the
            // detail page never passed a handler, so every thumbnail was a
            // button that did nothing. See [[issue-activity-trail.spec]].
            if (!onOpenPhoto) {
              return (
                <div key={image.id} className="shrink-0">
                  {thumbnail}
                </div>
              );
            }

            return (
              <button
                key={image.id}
                type="button"
                aria-label="Open photo full screen"
                onClick={() => onOpenPhoto(update.images, index)}
                className="shrink-0"
              >
                {thumbnail}
              </button>
            );
          })}
        </div>
      )}
    </li>
  );
}

/**
 * The date this update forecast, beside the status it was forecast with.
 *
 * The status and the date somebody forecast it by are one fact, read in one
 * glance. They used to be split across a pill and a `Target date:` line the
 * update sheet prepended into the remarks — legible only because the paragraph
 * preserves newlines, and left to the reader to assemble.
 *
 * The target yellow, as on the Issues list and the detail heading: the date's
 * own colour rather than a borrowed status colour, and the same on every row,
 * so it never reads as movement. An edit's "Edited" pill stays outline-only.
 *
 * It states the date and nothing more. Nothing here knows what the target was
 * before, and no row is read against the one above it, so a chip never reads as
 * a change — which is also why no row is marked as carrying the *current*
 * target. The newest one is current by the sort alone, and the detail page's
 * heading is the single place that says so.
 *
 * Absent rather than empty when the update carries no target — most rows will
 * not have one, and "No target date" would spend a line on every row to serve
 * the few that do — and absent too when the date cannot be read, rather than
 * rendering "Invalid Date" as an unreadable timestamp would.
 * See [[issue-activity-trail.spec]].
 */
function TargetDateChip({ value }: { value: string | null }) {
  return <IssueDatePill kind="target" value={value} tinted />;
}

/**
 * Someone amended the issue's header.
 *
 * A hollow dot and an outline pill rather than the status treatment: the row
 * carries a status underneath — every row does, which is what keeps the trail a
 * complete state log — but rendering it would claim a transition that did not
 * happen. Colour stays reserved for actual movement.
 *
 * One entry per save, a line per field. Three entries for one tap of Save would
 * read as three decisions. See [[issue-activity-trail.spec]].
 */
function EditEntry({ update }: { update: IssueUpdate }) {
  // Defensive `?? []`: the field is required by the contract, but a row that
  // arrives without it must still render as "somebody edited this" rather than
  // take the page down. The trail is the one surface that cannot go blank.
  const changes = update.changes ?? [];

  return (
    <li className="relative pb-5 last:pb-0">
      <span
        className="absolute -left-[26px] top-1 h-2.5 w-2.5 rounded-full border-[1.5px] border-muted-foreground/60 bg-background ring-[3px] ring-background"
        aria-hidden="true"
      />
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[13px] font-medium tracking-[-0.005em]">
          {personName(update.author)}
        </span>
        <span className="inline-flex shrink-0 items-center whitespace-nowrap rounded-full border border-border px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
          Edited
        </span>
      </div>
      <Timestamp value={update.created_at} />
      {changes.map((change, index) => (
        <ChangeLine
          // Nothing on a change is a stable id, and the server writes the list
          // once and never mutates it — so the position within this entry is
          // genuinely the row's identity.
          key={`${change.field}-${index}`}
          change={change}
        />
      ))}
    </li>
  );
}

/**
 * The four field names the backend emits, in the words the trail uses.
 *
 * These are model field names and the set is closed: `IssueEditChangeContractTests`
 * on the backend asserts all four literally, `assigned_to` included, so the
 * writable `assigned_to_id` never reaches this map and is not read for.
 *
 * Anything outside the four is humanised rather than dropped. Not
 * alias-tolerance — a fifth editable field added later must still render, and a
 * change the trail cannot name is still a change someone made. Hiding it is the
 * one failure the trail cannot afford.
 */
const CHANGE_LABELS: Record<string, string> = {
  title: "Title",
  description: "Description",
  assigned_to: "Assignee",
  due_date: "Due date",
};

function changeLabel(field: string): string {
  const known = CHANGE_LABELS[field];
  if (known) return known;
  const words = field.replace(/_/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : field;
}

/**
 * A title is quoted; nothing else is touched.
 *
 * Both sides of a title change are short enough to show in full, and the quotes
 * are what keep a title edited down to one word legible as a value rather than
 * as a stray fragment of the sentence around it.
 *
 * Every other value passes through verbatim — deliberately. `old` and `new` are
 * server-rendered display strings, so a date arrives as `30 Aug 2026` and is
 * never parsed or re-formatted here: the trail is history and must read
 * identically forever, which a client-side format would break the moment a
 * locale setting existed.
 */
function changeValue(field: string, value: string): string {
  return field === "title" ? `“${value}”` : value;
}

function ChangeLine({ change }: { change: IssueFieldChange }) {
  if (change.field === "description") {
    return <DescriptionChange change={change} />;
  }

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[12.5px]">
      <span className="font-medium">{changeLabel(change.field)}</span>
      <span className="text-muted-foreground">
        {changeValue(change.field, change.old)}
      </span>
      <ArrowRight
        className="h-3 w-3 shrink-0 text-muted-foreground"
        aria-hidden="true"
      />
      <span className="font-medium">
        {changeValue(change.field, change.new)}
      </span>
    </div>
  );
}

/**
 * A description change collapses to one line that opens.
 *
 * A paragraph either side would drown the trail, so the values move into a
 * panel the reader asks for. Whole values rather than a word-level diff: on a
 * phone in sunlight, two paragraphs beat highlighted fragments.
 * See [[issue-activity-trail.spec]].
 */
function DescriptionChange({ change }: { change: IssueFieldChange }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="mt-1.5 text-[12.5px]">
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
        <span className="font-medium">Description</span>
        <span className="text-muted-foreground">edited</span>
        <button
          type="button"
          onClick={() => setOpen((current) => !current)}
          aria-expanded={open}
          className="font-medium text-tertiary-ink"
        >
          {open ? "Hide change" : "View change"}
        </button>
      </div>
      {open && (
        <div className="mt-2 rounded-lg border border-border bg-card p-3">
          <BeforeAfter label="Before" value={change.old} muted />
          <div className="my-3 h-px bg-border" />
          <BeforeAfter label="After" value={change.new} />
        </div>
      )}
    </div>
  );
}

/**
 * One side of a description change.
 *
 * A description edited from nothing arrives as `old: ""` — an empty string, not
 * a placeholder. Only `assigned_to` and `due_date` nulls are given words
 * server-side ("Unassigned", "No due date"), so this side has to supply its own
 * or the panel renders a heading over a collapsed paragraph and reads as a bug.
 * Italic marks it as a rendered state rather than as text somebody typed.
 */
function BeforeAfter({
  label,
  value,
  muted = false,
}: {
  label: string;
  value: string;
  muted?: boolean;
}) {
  const empty = value.trim() === "";
  return (
    <div>
      <span className="block text-[11px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">
        {label}
      </span>
      <p
        className={cn(
          "mt-1.5 whitespace-pre-line text-[13px] leading-[19px] [text-wrap:pretty]",
          empty
            ? "italic text-muted-foreground"
            : muted
              ? "text-muted-foreground"
              : "text-foreground",
        )}
      >
        {empty ? "No description" : value}
      </p>
    </div>
  );
}

function TrailEntry({
  dotClass,
  heading,
  timestamp,
}: {
  dotClass: string;
  heading: string;
  timestamp: string | null;
}) {
  return (
    <li className="relative pb-5 last:pb-0">
      <span
        className={cn(
          "absolute -left-[26px] top-1 h-2.5 w-2.5 rounded-full ring-[3px] ring-background",
          dotClass,
        )}
        aria-hidden="true"
      />
      <span className="text-[13px] font-medium tracking-[-0.005em]">
        {heading}
      </span>
      <Timestamp value={timestamp} />
    </li>
  );
}

const DOT_CLASS: Record<string, string> = {
  assigned: "bg-muted-foreground/50",
  in_progress: "bg-warning",
  completed: "bg-tertiary",
  closed: "bg-primary",
};

function Dot({ status }: { status: string }) {
  return (
    <span
      className={cn(
        "absolute -left-[26px] top-1 h-2.5 w-2.5 rounded-full ring-[3px] ring-background",
        DOT_CLASS[status] ?? "bg-muted-foreground/50",
      )}
      aria-hidden="true"
    />
  );
}

function Timestamp({ value }: { value: string | null }) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return (
    <time dateTime={value} className="block text-[11px] text-muted-foreground">
      {date.toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })}
    </time>
  );
}
